import type {
  CreateDeliveryInput,
  CreateVehicleInput,
  DeliveryType,
  UpdateDeliveryInput,
  UpdateVehicleInput,
} from "@/libs/generated/types";
import type { DeliveryFormValues, VehicleValues } from "../types";

const TYPE_MAP: Record<string, DeliveryType> = {
  standard: "STANDARD",
  "to-return": "TO_RETURN",
  "two-way-delivery": "TWO_WAY_DELIVERY",
  "quality-check": "QUALITY_CHECK",
  okm: "OKM",
};

function toVehicleInput(vehicle: VehicleValues, toRetrieve: boolean): CreateVehicleInput {
  const { type, carType, id: _id, ...fields } = vehicle;
  if (type !== "GAS" && type !== "HYBRID" && type !== "ELECTRIC") {
    throw new Error("Select a valid vehicle fuel type");
  }
  return { ...fields, type, carType: carType || undefined, toRetrieve };
}

// ─── Create Input ───────────────────────────────────────────────────────────

export function transformToCreateInput(
  values: DeliveryFormValues,
  type: string
): CreateDeliveryInput {
  const { step0, step1, step2, lastStep } = values;
  const isTwoWay = type === "two-way-delivery";

  const vehicles = [toVehicleInput(step1.vehicle, false)];
  if (isTwoWay && step2.returnVehicle.carType) {
    vehicles.push(toVehicleInput(step2.returnVehicle, true));
  }

  return {
    type: TYPE_MAP[type] ?? "STANDARD",
    status: "PROFESSIONAL",
    numberOfVehicles: 1,

    // Client
    clientFirstName: step0.clientFirstName,
    clientLastName: step0.clientLastName,
    clientEmail: step0.clientEmail,
    clientPhone: step0.clientPhone.replace(/\s/g, ""),
    clientCompany: step0.clientCompany,
    clientSiret: step0.clientSiret,
    clientDrivingLicenceNumber: step0.clientDrivingLicenceNumber,

    // Mandataire / Representative
    representativeFirstName: step0.representativeFirstName,
    representativeLastName: step0.representativeLastName,
    representativeEmail: step0.representativeEmail,
    representativePhone: step0.representativePhone.replace(/\s/g, ""),
    representativeCompany: step0.representativeCompany,
    representativeSiret: step0.representativeSiret || "", // Fallback to empty string

    // IDs
    ...(step0.selectedMandataireId && {
      clientId: Number(step0.selectedMandataireId),
    }),

    // Dates
    carAvailabilityDate: step0.carAvailabilityDate,
    carPickUpDate: step0.carPickUpDate,
    carDeliveryDeadline: step0.carDeliveryDeadline,

    // Addresses
    fromAddress: step1.fromAddress,
    toAddress: step1.toAddress,
    ...(isTwoWay &&
      step2.toReturnFromAddress.address && {
        toReturnFromAddress: step2.toReturnFromAddress,
      }),
    ...(isTwoWay &&
      step2.toReturnToAddress.address && {
        toReturnToAddress: step2.toReturnToAddress,
      }),

    // Vehicles
    vehicles,

    // Options
    selectedOptions: lastStep.selectedOptions.selected.map((opt) => ({
      optionId: Number(opt.id),
      ...(opt.file ? { file: opt.file } : {}),
      ...(opt.input ? { input: opt.input } : {}),
    })),

    // Misc
    category: lastStep.category,
    comment: lastStep.comment,
    docs: lastStep.docs,
  };
}

// ─── Update Input ───────────────────────────────────────────────────────────

export function transformToUpdateInput(
  values: DeliveryFormValues,
  deliveryId: number,
  type: string
): UpdateDeliveryInput {
  const base = transformToCreateInput(values, type);

  // Strip fields not allowed in update
  const { status, slug, clientId, vehicles: _vehicles, ...updateFields } = base;
  const sourceVehicles = [values.step1.vehicle];
  if (type === "two-way-delivery" && values.step2.returnVehicle.carType) {
    sourceVehicles.push(values.step2.returnVehicle);
  }
  const vehicles = sourceVehicles.map((vehicle, index): UpdateVehicleInput => {
    if (vehicle.id == null) throw new Error("Cannot update a vehicle without its ID");
    return { ...toVehicleInput(vehicle, index > 0), id: vehicle.id };
  });
  return { id: deliveryId, ...updateFields, vehicles };
}
